import {
  AlertCircle,
  CalendarDays,
  Check,
  ChevronDown,
  Plus,
  RefreshCw,
  UserPlus,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

export interface SyncActionResult {
  success: boolean;
  message: string;
}

interface MakersBoardActionsProps {
  oooEnabled: boolean;
  oooEditMode: boolean;
  onAddEmployee: () => void;
  onToggleOoo: () => void;
  syncEmployees: () => Promise<SyncActionResult>;
  syncCalendar: () => Promise<SyncActionResult>;
}

type Menu = "add" | "sync";
type SyncKind = "employees" | "calendar";

interface SyncState {
  startedAt: number | null;
  result: SyncActionResult | null;
}

const INITIAL_SYNC_STATE: Record<SyncKind, SyncState> = {
  employees: { startedAt: null, result: null },
  calendar: { startedAt: null, result: null },
};

function formatElapsed(startedAt: number, now: number): string {
  const elapsedSeconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function MakersBoardActions({
  oooEnabled,
  oooEditMode,
  onAddEmployee,
  onToggleOoo,
  syncEmployees,
  syncCalendar,
}: MakersBoardActionsProps) {
  const [openMenu, setOpenMenu] = useState<Menu | null>(null);
  const [syncState, setSyncState] = useState<Record<SyncKind, SyncState>>(
    INITIAL_SYNC_STATE,
  );
  const [clock, setClock] = useState(() => Date.now());
  const runningRef = useRef<Record<SyncKind, boolean>>({
    employees: false,
    calendar: false,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const employeesRunning = syncState.employees.startedAt !== null;
  const calendarRunning = syncState.calendar.startedAt !== null;
  const runningCount = Number(employeesRunning) + Number(calendarRunning);

  useEffect(() => {
    if (runningCount === 0) return;
    setClock(Date.now());
    const interval = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [runningCount]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const elapsed = useMemo(
    () => ({
      employees:
        syncState.employees.startedAt === null
          ? null
          : formatElapsed(syncState.employees.startedAt, clock),
      calendar:
        syncState.calendar.startedAt === null
          ? null
          : formatElapsed(syncState.calendar.startedAt, clock),
    }),
    [clock, syncState],
  );

  const startSync = async (kind: SyncKind) => {
    if (runningRef.current[kind]) return;
    runningRef.current[kind] = true;
    setClock(Date.now());
    setSyncState((current) => ({
      ...current,
      [kind]: { startedAt: Date.now(), result: null },
    }));

    try {
      const result = await (kind === "employees"
        ? syncEmployees()
        : syncCalendar());
      setSyncState((current) => ({
        ...current,
        [kind]: { startedAt: null, result },
      }));
    } catch (error) {
      setSyncState((current) => ({
        ...current,
        [kind]: {
          startedAt: null,
          result: {
            success: false,
            message: error instanceof Error ? error.message : "Unknown error",
          },
        },
      }));
    } finally {
      runningRef.current[kind] = false;
    }
  };

  const syncButtonLabel = (() => {
    if (runningCount === 2) return "2 syncing";
    if (employeesRunning) return `Employees · ${elapsed.employees ?? "0:00"}`;
    if (calendarRunning) return `Calendar · ${elapsed.calendar ?? "0:00"}`;
    return "Sync";
  })();

  const renderSyncIcon = (kind: SyncKind) => {
    const running = kind === "employees" ? employeesRunning : calendarRunning;
    const result = syncState[kind].result;
    if (running) {
      return <RefreshCw className="makers-board__action-spinner" aria-hidden />;
    }
    if (result?.success) return <Check aria-hidden />;
    if (result && !result.success) return <AlertCircle aria-hidden />;
    return kind === "employees" ? (
      <RefreshCw aria-hidden />
    ) : (
      <CalendarDays aria-hidden />
    );
  };

  const renderSyncMeta = (kind: SyncKind) => {
    const running = kind === "employees" ? employeesRunning : calendarRunning;
    const result = syncState[kind].result;
    const source = kind === "employees" ? "BambooHR" : "Google Calendar";
    const elapsedLabel = elapsed[kind];
    if (running) return `${source} · Syncing · ${elapsedLabel ?? "0:00"}`;
    if (result?.success) return `${source} · Completed`;
    if (result && !result.success) return `${source} · Failed`;
    return source;
  };

  return (
    <div className="makers-board__actions" ref={containerRef}>
      <button
        type="button"
        className={`makers-board__action-btn ${oooEditMode ? "makers-board__action-btn--active" : ""}`}
        onClick={() =>
          setOpenMenu((current) => (current === "add" ? null : "add"))
        }
        aria-haspopup="menu"
        aria-expanded={openMenu === "add"}
        aria-pressed={oooEditMode}
      >
        <Plus aria-hidden />
        <span>{oooEditMode ? "Adding OOO" : "Add"}</span>
        <ChevronDown aria-hidden />
      </button>

      <button
        type="button"
        className="makers-board__action-btn"
        onClick={() =>
          setOpenMenu((current) => (current === "sync" ? null : "sync"))
        }
        aria-haspopup="menu"
        aria-expanded={openMenu === "sync"}
      >
        <RefreshCw
          className={runningCount > 0 ? "makers-board__action-spinner" : undefined}
          aria-hidden
        />
        <span>{syncButtonLabel}</span>
        <ChevronDown aria-hidden />
      </button>

      {openMenu === "add" && (
        <div className="makers-board__action-menu" role="menu" aria-label="Add">
          <span className="makers-board__action-menu-label">Create</span>
          <button
            type="button"
            className="makers-board__action-menu-item"
            role="menuitem"
            onClick={() => {
              setOpenMenu(null);
              onAddEmployee();
            }}
          >
            <span className="makers-board__action-menu-icon">
              <UserPlus aria-hidden />
            </span>
            <span className="makers-board__action-menu-copy">
              <strong>Add employee</strong>
              <small>Create a new maker record</small>
            </span>
          </button>
          {oooEnabled && (
            <button
              type="button"
              className="makers-board__action-menu-item"
              role="menuitem"
              onClick={() => {
                setOpenMenu(null);
                onToggleOoo();
              }}
            >
              <span className="makers-board__action-menu-icon">
                <CalendarDays aria-hidden />
              </span>
              <span className="makers-board__action-menu-copy">
                <strong>{oooEditMode ? "Done adding OOO" : "Add OOO"}</strong>
                <small>
                  {oooEditMode
                    ? "Exit out-of-office mode"
                    : "Enter out-of-office mode"}
                </small>
              </span>
            </button>
          )}
        </div>
      )}

      {openMenu === "sync" && (
        <div className="makers-board__action-menu" role="menu" aria-label="Sync data">
          <span className="makers-board__action-menu-label">Sync data</span>
          {(["employees", "calendar"] as const).map((kind) => {
            const running =
              kind === "employees" ? employeesRunning : calendarRunning;
            const result = syncState[kind].result;
            return (
              <button
                key={kind}
                type="button"
                className="makers-board__action-menu-item"
                role="menuitem"
                disabled={running}
                onClick={() => void startSync(kind)}
              >
                <span className="makers-board__action-menu-icon">
                  {renderSyncIcon(kind)}
                </span>
                <span
                  className="makers-board__action-menu-copy"
                  aria-live="polite"
                >
                  <strong>
                    {kind === "employees" ? "Employees" : "Calendar OOO"}
                  </strong>
                  <small>{renderSyncMeta(kind)}</small>
                  {result && (
                    <small
                      className={
                        result.success
                          ? "makers-board__action-result--success"
                          : "makers-board__action-result--error"
                      }
                    >
                      {result.message}
                    </small>
                  )}
                </span>
              </button>
            );
          })}
          {runningCount > 0 && (
            <span className="makers-board__action-menu-footer">
              You can keep working while syncs run.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
